import React, { useId, useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import { uploadJobPhotoFile } from "../../lib/dashboardJobPhotosClient";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import type { RouteMap } from "../../pages/MainAppPage/types";

const ACCEPTED_MEDIA =
  "image/jpeg,image/jpg,image/png,image/webp,image/heic,video/mp4,video/quicktime";

export type JobPhotoUploadFieldProps = {
  jobId: string;
  csrfToken: string;
  routes: Pick<
    RouteMap,
    "dashboardJobPhotosUploadUrl" | "dashboardJobPhotosConfirm"
  >;
  onUploaded?: (files: File[]) => void;
  className?: string;
};

export const JobPhotoUploadField: React.FC<JobPhotoUploadFieldProps> = ({
  jobId,
  csrfToken,
  routes,
  onUploaded,
  className,
}) => {
  const autoId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: File[]) => {
    if (files.length === 0 || uploading) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploaded: File[] = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        if (!file) {
          continue;
        }
        setStatus(`Uploading ${index + 1} of ${files.length}…`);
        await uploadJobPhotoFile({
          file,
          jobId,
          csrfToken,
          routes,
        });
        uploaded.push(file);
      }
      setStatus("Uploaded.");
      onUploaded?.(uploaded);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not upload that file.",
      );
      setStatus(null);
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  return (
    <div className={cn("relative space-y-3", className)}>
      <div>
        <p className="ds-form-label">Upload pictures</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Photos or video from this computer. The Toolkit app is optional.
        </p>
      </div>
      <input
        ref={inputRef}
        id={autoId}
        type="file"
        accept={ACCEPTED_MEDIA}
        multiple
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        disabled={uploading}
        onChange={(event) => {
          void handleFiles(Array.from(event.target.files ?? []));
        }}
      />
      <Button
        type="button"
        variant="outline"
        className="w-full justify-center gap-2 sm:w-auto"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        <ImagePlus className="h-4 w-4" />
        {uploading ? "Uploading…" : "Upload from computer"}
      </Button>
      {status ? (
        <p className="text-sm text-muted-foreground" role="status">
          {status}
        </p>
      ) : null}
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};
